import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiBars3, HiXMark, HiChevronDown, HiArrowUpRight } from 'react-icons/hi2';
import {
  HiOutlineLightBulb, HiOutlineCpuChip, HiOutlineScissors,
  HiOutlineWrenchScrewdriver, HiOutlineShoppingCart, HiOutlineTruck,
  HiOutlineWrench
} from 'react-icons/hi2';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleDropdown = (name) => {
    setActiveDropdown(prev => (prev === name ? null : name));
  };

  const isGeneratorActive = location.pathname.startsWith('/generator-');
  const isDivisionsActive = ['/solar-solutions', '/electrical-panels', '/cutting-tools', '/generator-solutions-overview'].includes(location.pathname);

  const isHomePage = location.pathname === '/';
  const showSolidNavbar = !isHomePage || isScrolled || mobileMenuOpen;

  const navLinkClass = ({ isActive }) =>
    `relative py-1.5 text-sm font-semibold whitespace-nowrap transition-colors duration-300 ${
      isActive
        ? showSolidNavbar
          ? 'text-primary nav-link-active'
          : 'text-accent nav-link-active'
        : showSolidNavbar
          ? 'text-heading/85'
          : 'text-white/90'
    }`;

  /* ---- Dropdown Menu Data ---- */
  const divisionsMenu = [
    { to: '/solar-solutions', label: 'Solar Solutions', sub: 'On-grid, off-grid & hybrid systems', icon: <HiOutlineLightBulb className="w-4 h-4" /> },
    { to: '/generator-solutions-overview', label: 'Generator Solutions', sub: 'Diesel & silent gensets overview', icon: <HiOutlineWrenchScrewdriver className="w-4 h-4" /> },
    { to: '/electrical-panels', label: 'Electrical Panels', sub: 'Synchronizing & distribution panels', icon: <HiOutlineCpuChip className="w-4 h-4" /> },
    { to: '/cutting-tools', label: 'Cutting Tools', sub: 'Industrial precision tooling', icon: <HiOutlineScissors className="w-4 h-4" /> },
  ];

  const generatorMenu = [
    { to: '/generator-sales', label: 'Generator Sales', sub: 'Authorized TMTL silent genset range', icon: <HiOutlineShoppingCart className="w-4 h-4" /> },
    { to: '/generator-rental', label: 'Generator Rental', sub: 'Flexible temporary & project backup', icon: <HiOutlineTruck className="w-4 h-4" /> },
    { to: '/generator-maintenance', label: 'Maintenance & AMC', sub: 'Scheduled PM & rapid breakdown support', icon: <HiOutlineWrench className="w-4 h-4" /> },
  ];

  const dropdownVariants = {
    hidden: { opacity: 0, y: 8, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1] } },
    exit: { opacity: 0, y: 6, scale: 0.97, transition: { duration: 0.12 } },
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolidNavbar
          ? 'bg-white py-3 border-b border-border/60 shadow-md shadow-black/10'
          : 'bg-transparent py-5 navbar-gradient'
      }`}
    >
      <div className="w-full max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between">

        {/* ---- Brand Logo ---- */}
        {/* ---- Brand Logo ---- */}
        <div className={`bg-white flex items-center justify-center border border-border/10 transition-all duration-300 ${
          showSolidNavbar
            ? 'px-3 py-1.5 md:px-4 md:py-2 rounded-xl shadow-sm'
            : 'px-4 py-2.5 md:px-5 md:py-3.5 rounded-2xl shadow-lg'
        }`}>
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/Green Gusto.png"
              alt="Green Gusto Logo"
              className={`w-auto object-contain transition-all duration-300 ${
                showSolidNavbar
                  ? 'h-10 md:h-14'
                  : 'h-14 md:h-18'
              }`}
            />
          </Link>
        </div>

        {/* ---- Desktop Navigation ---- */}
        <div className="hidden xl:flex items-center gap-8">
          <nav className="flex items-center gap-7">

            {/* Home */}
            <NavLink to="/" end className={navLinkClass}>
              Home
            </NavLink>

            {/* About */}
            <NavLink to="/about" className={navLinkClass}>
              About Us
            </NavLink>

            {/* Divisions Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('divisions')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-300 py-1.5 whitespace-nowrap ${
                  isDivisionsActive
                    ? showSolidNavbar
                      ? 'text-primary'
                      : 'text-accent'
                    : showSolidNavbar
                      ? 'text-heading/85'
                      : 'text-white/90'
                }`}
              >
                Divisions
                <HiChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === 'divisions' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {activeDropdown === 'divisions' && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute left-0 top-full mt-2.5 w-72 bg-white rounded-2xl shadow-premium border border-border/60 overflow-hidden"
                  >
                    <div className="p-2">
                      {divisionsMenu.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="dropdown-item"
                        >
                          <div>
                            <div className="text-xs font-bold text-heading transition-colors">{item.label}</div>
                            <div className="text-[10px] text-body-muted leading-tight mt-0.5">{item.sub}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Generator Solutions Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('generator')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-300 py-1.5 whitespace-nowrap ${
                  isGeneratorActive
                    ? showSolidNavbar
                      ? 'text-primary'
                      : 'text-accent'
                    : showSolidNavbar
                      ? 'text-heading/85'
                      : 'text-white/90'
                }`}
              >
                Generator Solutions
                <HiChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === 'generator' ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {activeDropdown === 'generator' && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-2.5 w-72 bg-white rounded-2xl shadow-premium border border-border/60 overflow-hidden"
                  >
                    <div className="p-2">
                      {generatorMenu.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="dropdown-item"
                        >
                          <div>
                            <div className="text-xs font-bold text-heading transition-colors">{item.label}</div>
                            <div className="text-[10px] text-body-muted leading-tight mt-0.5">{item.sub}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavLink to="/telecom" className={navLinkClass}>Telecom Services</NavLink>
            <NavLink to="/gallery" className={navLinkClass}>Gallery</NavLink>
            <NavLink to="/clients" className={navLinkClass}>Industries</NavLink>
            <NavLink to="/careers" className={navLinkClass}>Careers</NavLink>
            <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
          </nav>

          {/* CTA Button */}
          <Link
            to="/contact#enquiry"
            className="btn-nav-cta rounded-full text-xs uppercase tracking-wide"
          >
            Request Quote
            <HiArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* ---- Mobile Toggle ---- */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`xl:hidden p-2.5 rounded-xl transition-colors duration-200 ${
            showSolidNavbar
              ? 'text-heading/70'
              : 'text-white'
          }`}
          aria-label="Toggle navigation"
        >
          <AnimatePresence mode="wait">
            {mobileMenuOpen
              ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <HiXMark className="w-6 h-6" />
                </motion.div>
              : <motion.div key="bars" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <HiBars3 className="w-6 h-6" />
                </motion.div>
            }
          </AnimatePresence>
        </button>
      </div>

      {/* ---- Mobile Sidebar Drawer ---- */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="xl:hidden fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
            />

            {/* Sidebar Drawer Container */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="xl:hidden fixed top-0 right-0 h-full w-[85%] sm:w-[60%] md:w-[50%] max-w-[340px] sm:max-w-[420px] bg-white z-[101] shadow-2xl flex flex-col justify-between overflow-hidden"
            >
              {/* Header inside Drawer */}
              <div className="bg-[#0C1A12] text-white px-5 py-4 flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-1 rounded-lg">
                    <img src="/Green Gusto.png" alt="Logo" className="h-7 w-auto object-contain" />
                  </div>
                  <span className="text-[10px] font-bold tracking-widest uppercase">GREEN GUSTO</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white hover:text-accent p-1.5 transition-colors"
                >
                  <HiXMark className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Body Scrollable Content */}
              <div className="flex-grow overflow-y-auto">
                <div className="flex flex-col">
                  {/* Home */}
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    Home
                  </Link>

                  {/* About Us */}
                  <Link
                    to="/about"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    About Us
                  </Link>

                  {/* Divisions Dropdown */}
                  <div className="border-b border-border/60">
                    <button
                      onClick={() => toggleDropdown('mob-div')}
                      className="flex items-center justify-between w-full py-3.5 px-6 text-sm font-bold text-heading hover:bg-slate-50 transition-colors text-left"
                    >
                      <span>Divisions</span>
                      <HiChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'mob-div' ? 'rotate-180 text-primary' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === 'mob-div' && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-slate-50/50 border-t border-border/40 overflow-hidden"
                        >
                          {divisionsMenu.map(item => (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-2.5 pl-10 pr-6 text-xs font-bold text-body hover:text-primary transition-colors border-b border-border/20 last:border-0"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Generator Solutions Dropdown */}
                  <div className="border-b border-border/60">
                    <button
                      onClick={() => toggleDropdown('mob-gen')}
                      className="flex items-center justify-between w-full py-3.5 px-6 text-sm font-bold text-heading hover:bg-slate-50 transition-colors text-left"
                    >
                      <span>Generator Solutions</span>
                      <HiChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === 'mob-gen' ? 'rotate-180 text-primary' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === 'mob-gen' && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-slate-50/50 border-t border-border/40 overflow-hidden"
                        >
                          {generatorMenu.map(item => (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-2.5 pl-10 pr-6 text-xs font-bold text-body hover:text-primary transition-colors border-b border-border/20 last:border-0"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Telecom Services */}
                  <Link
                    to="/telecom"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    Telecom Services
                  </Link>

                  {/* Gallery */}
                  <Link
                    to="/gallery"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    Gallery
                  </Link>

                  {/* Industries */}
                  <Link
                    to="/clients"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    Industries
                  </Link>

                  {/* Careers */}
                  <Link
                    to="/careers"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    Careers
                  </Link>

                  {/* Contact Us */}
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-6 border-b border-border/60 text-sm font-bold text-heading hover:bg-slate-50 transition-colors"
                  >
                    Contact
                  </Link>
                </div>
              </div>

              {/* Footer inside Drawer */}
              <div className="p-5 border-t border-border bg-[#F8FAFC]">
                <Link
                  to="/contact#enquiry"
                  onClick={() => setMobileMenuOpen(false)}
                  className="btn-accent rounded-xl w-full justify-center py-3 text-xs uppercase tracking-wide flex items-center gap-2"
                >
                  Request Quote
                  <HiArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                <div className="mt-4 text-center text-xs font-bold text-heading/70 flex items-center justify-center gap-1.5">
                  <span className="text-body-muted">Contact:</span>
                  <a href="tel:+919609600600" className="hover:text-primary transition-colors text-primary font-extrabold">+91 96096 00600</a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
